import { FC, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { WritebackSettingMode } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { FieldProps, Heading, RadioCardField, Text, TextLink } from '@frontend/design-system';
import { CALL_INTEL_SYNC_HELP_LINK, SyncOptionsList } from '../../constants';
import { CallIntelSettingsTrackingIds } from '../../tracking-ids';

interface CallSummaryPmsSyncSettingsProps {
  containerStyles?: Interpolation<Theme>;
  fieldProps: FieldProps<{ syncOption: { type: 'radio' } }, 'syncOption'>;
  hideAutomaticSyncOption?: boolean;
}

export const CallSummaryPmsSyncSettings: FC<CallSummaryPmsSyncSettingsProps> = ({
  containerStyles,
  fieldProps,
  hideAutomaticSyncOption,
}) => {
  const { t } = useTranslation('settings', { keyPrefix: 'call-intel-settings' });

  const filteredSyncOptions = useMemo(() => {
    if (hideAutomaticSyncOption) {
      return SyncOptionsList.filter((option) => option.value !== WritebackSettingMode.WRITEBACK_SETTING_MODE_AUTOMATIC);
    }
    return SyncOptionsList;
  }, [hideAutomaticSyncOption]);

  return (
    <div css={[styles.card, containerStyles]}>
      <div>
        <Heading level={3}>{t('Call Summary')}</Heading>
        <Text color='light' as='span'>
          <Trans t={t}>
            Choose how AI call summaries are uploaded to the patient's record in your PMS or EHR. Where the summary
            appears in the patient record depends on your integration.{' '}
            <TextLink
              href={CALL_INTEL_SYNC_HELP_LINK}
              target='_blank'
              rel='noopener noreferrer'
              trackingId={CallIntelSettingsTrackingIds.pmsSync.syncSettingHelpLink}
            >
              Learn More
            </TextLink>
          </Trans>
        </Text>
      </div>
      <RadioCardField {...fieldProps} css={styles.radioField}>
        {filteredSyncOptions.map((option) => (
          <RadioCardField.Option
            key={option.value}
            title={option.label}
            description={option.description}
            value={option.value}
            trackingId={option.trackingId}
          />
        ))}
      </RadioCardField>
    </div>
  );
};

const styles = {
  card: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2.5)};
    padding: ${theme.spacing(3)};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
  `,
  radioField: css`
    > div {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(185px, 1fr));
      gap: ${theme.spacing(2)};
    }
  `,
};
